import { useApp } from 'contexts/app';
import { useViewport } from 'contexts/viewport';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, FormLabel, Icon, Paper, Stack, styled, Switch, Text, ViewMore } from 'shell/components';
import { isFeedbackSettingsDefault } from 'utils/helper';

const FeedbackSection = memo(
    ({ isEditable, form, setForm, errors, setFeedbackSettingsDialog, openVideoFeedbackSettings }) => {
        const { isFeedbackFeatureDisabled } = useApp();
        const { t } = useTranslation('assignment');
        const { device } = useViewport();

        const getText = () => {
            if (form.feedbackSettings.structure === 'rubric') {
                return t(
                    'feedback-section.rubric-based-feedback-with-feedbacktype',
                    'Rubric-based feedback ({{feedbackType}})',
                    {
                        feedbackType: isFeedbackSettingsDefault(form.feedbackSettings)
                            ? t('system-default', 'System Default')
                            : t('edited', 'Edited'),
                    }
                );
            }

            if (form.feedbackSettings.structure === 'system') {
                return t('feedback-section.system-based-feedback', 'General & detailed feedback');
            }

            if (form.feedbackSettings.structure === 'templated') {
                return errors.feedbackSettings
                    ? t('feedback-section.templated-feedback-with-error', 'Smart Feedback Templates {{error}}', {
                          error: errors.feedbackSettings,
                      })
                    : t('feedback-section.templated-feedback', 'Smart Feedback Templates');
            }

            return '';
        };

        const isAdvancedSettingsDisabled = !isEditable || !form.aiFeedbackEnabled;

        return (
            <Stack sx={{ width: '100%' }} spacing={1}>
                <Text variant={'p1'}>{t('feedback-section.feedback-label', 'Feedback')}</Text>
                <CustomPaper variant={'outlined'}>
                    <Stack spacing={4}>
                        <CustomPaper variant={'outlined'}>
                            <Stack spacing={3}>
                                <Stack
                                    alignItems={device === 'mobile' ? 'flex-start' : 'center'}
                                    direction={'row'}
                                    justifyContent={'space-between'}
                                >
                                    <Stack
                                        alignItems={device === 'mobile' ? 'flex-start' : 'center'}
                                        direction={device === 'mobile' ? 'column-reverse' : 'row'}
                                        spacing={device === 'mobile' ? 2 : 4}
                                    >
                                        <Text variant={'p1'}>{t('feedback-section.title', 'Feedback Settings')}</Text>
                                        <Button
                                            startIcon={<Icon kind={'video'} />}
                                            variant={'borderless'}
                                            onClick={() => openVideoFeedbackSettings()}
                                        >
                                            {t('feedback-section.watch-tutorial', 'Watch a Tutorial')}
                                        </Button>
                                    </Stack>
                                    <Button
                                        disabled={isAdvancedSettingsDisabled}
                                        variant={'borderless'}
                                        onClick={() => setFeedbackSettingsDialog({ open: true })}
                                    >
                                        {t('advanced-settings', 'Advanced Settings')}
                                    </Button>
                                </Stack>
                                {form.feedbackSettings.structure === 'custom' ? (
                                    <ViewMore TextComponentProps={{ color: 'light', rich: true }}>
                                        {form.feedbackSettings.customInstructions}
                                    </ViewMore>
                                ) : (
                                    <Text color={errors.feedbackSettings ? 'error' : 'light'} rich>
                                        {getText()}
                                    </Text>
                                )}
                            </Stack>
                        </CustomPaper>
                        <Stack spacing={2}>
                            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'} spacing={4}>
                                <FormLabel id={'feedback-switch'}>
                                    <Text variant={'p1'}>
                                        {t(
                                            'feedback-section.final-submission-feedback-suggestions',
                                            'Final Submission Feedback Suggestions'
                                        )}
                                    </Text>
                                </FormLabel>
                                <Switch
                                    aria-labelledby={'feedback-switch'}
                                    checked={form.aiFeedbackEnabled && !isFeedbackFeatureDisabled}
                                    disabled={!isEditable || isFeedbackFeatureDisabled}
                                    tooltip={
                                        isFeedbackFeatureDisabled
                                            ? {
                                                  component: t(
                                                      'institution-disabled',
                                                      'Disabled by your institution. Request access or contact IT.'
                                                  ),
                                              }
                                            : undefined
                                    }
                                    onChange={(_, value) => {
                                        setForm((current) => ({ ...current, aiFeedbackEnabled: value }));
                                    }}
                                />
                            </Stack>
                            <Text>
                                {t(
                                    'feedback-section.feedback-description',
                                    "TimelyGrader leverages assignment details to provide instructors with personalized feedback suggestions for student's final submissions."
                                )}
                            </Text>
                        </Stack>
                    </Stack>
                </CustomPaper>
            </Stack>
        );
    }
);

const CustomPaper = styled(Paper)(({ theme }) => {
    return {
        padding: theme.spacing(4),
    };
});

export default FeedbackSection;
